import View from '@play-co/timestep-core/lib/ui/View';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import uiConfig from 'src/lib/ui/config';
import bitmapFonts from 'src/lib/bitmapFonts';
import { openPopupPromise, closePopup } from 'src/lib/popups/popupOpenClose';
import PopupMonitor from '../../logic/PopupMonitor';
import { createEmitter } from 'src/lib/Emitter';
import { GCInstant } from 'src/lib/gcinstant';
import { devSettings } from 'src/lib/settings';
import { getGameScene } from 'src/game/minigame/match2-timestep/game/getGameScene';

const cheatPopups = [
  'popupCheats',
  'popupTestPopups',
  'popupCheatsAB',
  'popupMatch2Cheats',
] as const;
type CheatPopup = (typeof cheatPopups)[number];

const togglePopup = async (id: CheatPopup, args: unknown = {}) => {
  const openCheatPopups = cheatPopups.filter((id) => PopupMonitor.isOpen(id));
  openCheatPopups.forEach(closePopup);

  if (!openCheatPopups.includes(id)) {
    openPopupPromise(id, args as never);
  }
};

let cheatsButtonsRoot: View | null = null;

export default function createCheatsButton(opts: { superview: View }) {
  const container = new View({
    superview: opts.superview,
    zIndex: 20000, // on top of the world
    x: 15,
    width: 400,
    height: 60,
    centerOnOrigin: true,
    infinite: true,
    canHandleEvents: false,
    r: Math.PI / 2,
  });
  cheatsButtonsRoot = container;

  toggleCheatsButtonVisibility(!!devSettings.get('cheats'));

  // anchor elements
  createEmitter(container, ({ ui }) => ui.screenSize).addListener((screen) => {
    container.updateOpts({ y: (screen.height - 100) / 2 });
  });

  const line = new View({
    superview: container,
    x: 0,
    y: 0,
    width: container.style.width / 4 - 1,
    height: container.style.height,
    clip: true,
  });

  const cheatsContainer = new View({
    superview: container,
    x: 0,
    y: 0,
    width: container.style.width / 4 - 1,
    height: container.style.height,
    clip: true,
  });

  const labelOffsetY = GCInstant.osType === 'IOS' ? -2 : -8;

  new ButtonScaleViewWithText({
    superview: cheatsContainer,

    x: 0,
    y: 0,
    width: 120,
    height: 60,

    labelOffsetX: -10,
    labelOffsetY,
    localeText: () => 'Cheats',
    fontSize: 20,
    font: bitmapFonts('Body'),

    onClick: () => togglePopup('popupCheats'),

    ...uiConfig.buttons.primary,
    tracking: { feature: 'cheats', $subFeature: 'toggle-popup-cheats' },
  });

  const levelsContainer = new View({
    superview: container,

    x: container.style.width / 4 + 1,
    y: 0,
    width: container.style.width / 4 - 21,
    height: container.style.height,

    clip: true,
  });

  new ButtonScaleViewWithText({
    superview: levelsContainer,

    x: -21,
    y: 0,
    width: 120,
    height: 60,

    labelOffsetY,
    localeText: () => 'Level',
    fontSize: 20,
    font: bitmapFonts('Body'),
    onClick: async () => {
      const inGame = !!getGameScene();
      // Show level select if not in game, otherwise show in-game cheats
      return togglePopup('popupMatch2Cheats', {
        level: !inGame,
        gameplay: inGame,
      });
    },

    ...uiConfig.buttons.primary,
    tracking: { feature: 'cheats', $subFeature: 'toggle-popup-cheats-ab' },
  });

  const testsContainer = new View({
    superview: container,

    x: (container.style.width / 4) * 2 - 18,
    y: 0,
    width: container.style.width / 4 - 21,
    height: container.style.height,

    clip: true,
  });

  new ButtonScaleViewWithText({
    superview: testsContainer,

    x: -21,
    y: 0,
    width: 120,
    height: 60,

    labelOffsetY,
    localeText: () => 'Tests',
    fontSize: 20,
    font: bitmapFonts('Body'),
    onClick: () => togglePopup('popupCheatsAB'),

    ...uiConfig.buttons.primary,
    tracking: { feature: 'cheats', $subFeature: 'toggle-popup-cheats-ab' },
  });

  const popupCheatsContainer = new View({
    superview: container,

    x: (container.style.width / 4) * 3 - 36,
    y: 0,
    width: container.style.width / 4 - 3,
    height: container.style.height,

    clip: true,
  });

  new ButtonScaleViewWithText({
    superview: popupCheatsContainer,

    x: -21,
    y: 0,
    width: 116,
    height: 60,
    labelOffsetX: 5,
    labelOffsetY,
    fontSize: 20,
    font: bitmapFonts('Body'),

    localeText: () => 'Popups',
    onClick: () => togglePopup('popupTestPopups'),

    ...uiConfig.buttons.primary,
    tracking: { feature: 'cheats', $subFeature: 'toggle-popup-test-popups' },
  });
}

let cheatsStyleElement: HTMLStyleElement | null = null;

export function toggleCheatsButtonVisibility(visible: boolean) {
  cheatsButtonsRoot?.updateOpts({ visible });

  // Apply style at a page level, because the element might not be created yet.
  cheatsStyleElement ??= document.head.appendChild(
    document.createElement('style'),
  );
  cheatsStyleElement.innerHTML = `
  #debug-panel-start {
    display: ${visible ? 'flex' : 'none'} !important;
  }
`;
}

export { createCheatsButton };
